import { FestivalData, DataDiff } from '../model/FestivalData';
import { fetchCurrentVersion, updateFestivalData } from './api';
import { calculateDiff } from '../utils/diffCalculator';
import { ImageService } from './imageService';
import { formatDateWithoutMilliseconds } from '../utils/dateUtils';
import deepEqual from 'deep-equal';

const withoutUpdatedAt = ({ updated_at, ...data }: FestivalData) => data;

export class FestivalDataService {
  private imageService: ImageService;

  constructor(
    private sessionToken: string,
    private festivalId: string
  ) {
    this.imageService = new ImageService(sessionToken, festivalId);
  }

  async loadVersion(versionId: string): Promise<FestivalData> {
    return await fetchCurrentVersion(this.sessionToken, this.festivalId, versionId);
  }

  /**
   * What other people changed on the server since `lastKnownServerData` was read, or null if nothing.
   * `updated_at` only has second precision, so the content is compared as well: two saves within the same
   * second share a stamp.
   */
  async checkForConflicts(currentData: FestivalData, lastKnownServerData: FestivalData | null): Promise<DataDiff[] | null> {
    if (currentData.updated_at === null) return null;

    const serverVersion = await this.loadVersion(currentData.version.toString());
    const base = lastKnownServerData ?? currentData;
    const changedOnServer = serverVersion.updated_at !== currentData.updated_at
      || !deepEqual(withoutUpdatedAt(serverVersion), withoutUpdatedAt(base));
    if (!changedOnServer) return null;

    const diffs = calculateDiff(base, serverVersion).filter(diff => diff.path !== 'updated_at');
    return diffs.length > 0 ? diffs : null;
  }

  async saveVersion(data: FestivalData): Promise<{ updated_at: string }> {
    console.log("Uploading images...");
    const dataWithUploadedImages = await this.imageService.uploadAllImages(data);

    // Format dates
    const dataWithFormattedDates = {
      ...dataWithUploadedImages,
      shows: dataWithUploadedImages.shows.map(show => ({
        ...show,
        start_date: formatDateWithoutMilliseconds(show.start_date),
        end_date: formatDateWithoutMilliseconds(show.end_date)
      }))
    };

    console.log("Images uploaded, saving data...");
    
    return await updateFestivalData(
      this.sessionToken,
      this.festivalId,
      dataWithFormattedDates.version.toString(),
      dataWithFormattedDates
    );
  }

  getImageUrl(identifier: string): string {
    return this.imageService.getImageUrl(identifier);
  }
} 